import { Link } from 'react-router-dom';
import { FormProvider } from 'react-hook-form';
import { IonicButton } from 'ionic-ui';
import DialogTitle from '../../../../DialogTitle';
import { useWalletIndividualKyc } from '../../../../../../services/walletSetup/hooks/useWalletIndividualKyc';
import IndividualAddressVerificationDialog from './steps/IndividualAddressVerificationDialog';
import IndividualProfileDialog from './steps/IndividualProfileDialog';
import IndividualWalletCreationDialog from './steps/IndividualWalletCreationDialog';
import { WalletSetupResume } from '../../../../../../types/wallet/Walletkyc';
import { IndividualSecurityDialog } from './steps/IndividualSecurityDialog';
import clsx from 'clsx';
import { IonIcon } from '@ionic/react';
import { checkmarkCircleOutline } from 'ionicons/icons';

type Props = {
  setupResume?: WalletSetupResume | null;
};

// Address verification (meter number + disco code) is Tier 3 only — not required for Tier 2 wallet creation
const STEP_LABELS: Record<number, string> = {
  0: 'Wallet Profile',
  1: 'Verify NIN & BVN',
  2: 'Create wallet',
  3: 'Upgrade to Tier 3',
};

const formSteps = Object.keys(STEP_LABELS);

export default function WalletIndividualKycDialog(props: Props) {
  const {
    methods,
    step: currentStep,
    next,
    skip,
    addressList,
    hasStore,
    handleFormSubmission,
  } = useWalletIndividualKyc({ setupResume: props.setupResume });

  if (!hasStore)
    return (
      <div className="space-y-2 sm:max-w-[480px] dialog-content text-center">
        <>
          <h5 className="font-medium high-emphasis-text">
            Your account is currently inactive
          </h5>
          <p className="medium-emphasis-text">
            <Link
              to="/dashboard/get-started"
              className="font-medium underline text-primary"
            >
              Create your store and complete your profile
            </Link>
            <span> to continue it.</span>
          </p>
        </>
      </div>
    );

  const formStepIndicatorStyle = (idx: number) =>
    clsx(
      'size-8 rounded-full flex items-center justify-center mb-2',
      currentStep > idx && 'bg-green-100 text-green-600',
      currentStep === idx && 'bg-blue-100 text-blue-600',
      currentStep < idx && 'bg-gray-100 text-gray-400'
    );

  return (
    <div className="space-y-4 sm:max-w-[480px] dialog-content">
      <DialogTitle>Individual wallet setup</DialogTitle>
      <div className="py-3 px-6">
        <div className="flex justify-around items-center mb-4">
          {formSteps.map((_, idx) => (
            <div
              key={`individual-form-step-${idx}`}
              className="flex flex-col items-center"
            >
              <div className={formStepIndicatorStyle(idx)}>
                {currentStep > idx ? (
                  <IonIcon icon={checkmarkCircleOutline} className="size-5" />
                ) : (
                  <span className="text-sm font-medium">{idx + 1}</span>
                )}
              </div>
              <span className="max-sm:hidden text-xs font-medium text-text-dark-medium dark:text-text-light-medium">
                {STEP_LABELS[idx]}
              </span>
            </div>
          ))}
        </div>
        {/* <div className="relative">
          <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-200 -translate-y-1/2" />
          <div
            className="absolute top-1/2 left-0 h-0.5 bg-primary -translate-y-1/2 transition-all duration-300"
            style={{
              width: `${((currentStep - 1) / (steps.length - 1)) * 100}%`,
            }}
          />
        </div> */}
      </div>
      <FormProvider {...methods}>
        <form onSubmit={handleFormSubmission}>
          <>
            {currentStep === 0 && (
              <IndividualProfileDialog addresses={addressList} />
            )}
          </>
          <>{currentStep === 1 && <IndividualSecurityDialog />}</>
          <>{currentStep === 2 && <IndividualWalletCreationDialog />}</>
          <>
            {currentStep === 3 && (
              <IndividualAddressVerificationDialog addresses={addressList} />
            )}
          </>

          <div className="flex gap-2 justify-end flex-wrap">
            {currentStep === 3 ? (
              <>
                <IonicButton
                  className="flex-1 min-w-fit"
                  variant="outline"
                  type="button"
                  onClick={skip}
                >
                  Skip
                </IonicButton>
                <IonicButton
                  className="flex-1 min-w-fit"
                  type="button"
                  onClick={next}
                >
                  {STEP_LABELS[currentStep]}
                </IonicButton>
              </>
            ) : currentStep < 2 ? (
              <IonicButton
                className="flex-1 min-w-fit"
                type="button"
                onClick={next}
              >
                {STEP_LABELS[currentStep]}
              </IonicButton>
            ) : (
              <IonicButton className="flex-1 min-w-fit" type="submit">
                {STEP_LABELS[currentStep]}
              </IonicButton>
            )}
          </div>
        </form>
      </FormProvider>
    </div>
  );
}
